import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Loader2, IndianRupee, FileText, Trash2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadBlob, filenameFromHeader, errorMessage } from "@/lib/api";

const monthStr = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 7);
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const PayrollPanel = () => {
  const [staff, setStaff] = useState([]);
  const [month, setMonth] = useState(monthStr());
  const [selected, setSelected] = useState("");
  const [preview, setPreview] = useState(null);
  const [bonus, setBonus] = useState("");
  const [deductions, setDeductions] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [slips, setSlips] = useState([]);

  const loadStaff = async () => {
    try { const { data } = await api.get("/admin/staff"); setStaff(data); if (data[0]) setSelected(data[0].email); }
    catch { toast.error("Failed to load staff."); }
  };
  const loadSlips = async () => {
    try { const { data } = await api.get("/admin/payslips", { params: { month } }); setSlips(data); } catch {}
  };
  useEffect(() => { loadStaff(); }, []);
  useEffect(() => { loadSlips(); /* eslint-disable-next-line */ }, [month]);

  const runPreview = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payroll/preview", { params: { staff_email: selected, month } });
      setPreview(data);
    } catch { toast.error("Could not compute preview."); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (selected) runPreview(); /* eslint-disable-next-line */ }, [selected, month]);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post("/admin/payslips", {
        staff_email: selected, month,
        bonus: parseFloat(bonus) || 0, deductions: parseFloat(deductions) || 0, note,
      });
      toast.success("Payslip generated.");
      setBonus(""); setDeductions(""); setNote("");
      loadSlips();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not generate."); }
    finally { setLoading(false); }
  };

  const removeSlip = async (p) => {
    if (!window.confirm(`Delete payslip for ${p.staff_name || p.staff_email} · ${p.month}?`)) return;
    try {
      await api.delete(`/admin/payslips/${p.id}`);
      toast.success("Payslip deleted.");
      setSlips((cur) => cur.filter((x) => x.id !== p.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete."); }
  };

  const downloadSlip = async (p) => {
    try {
      const res = await api.get(`/admin/payslips/${p.id}/pdf`, { responseType: "blob" });
      const email = (p.staff_email || "staff").split("@")[0];
      const name = filenameFromHeader(res.headers, `payslip_${email}_${p.month}.pdf`);
      downloadBlob(res.data, name);
    } catch (err) { toast.error(await errorMessage(err, "Could not download PDF.")); }
  };

  const net = preview ? (preview.net_pay - 0 + (parseFloat(bonus) || 0) - (parseFloat(deductions) || 0)) : 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="payroll-panel">
      <Card className="border-0 rounded-3xl shadow-ark-soft lg:col-span-1 h-fit">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-display font-bold text-lg text-ark-ink flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-ark-teal" /> Generate Payslip
          </h3>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Staff</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="payroll-staff-select" className="rounded-2xl h-11 border-slate-200"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => <SelectItem key={s.email} value={s.email}>{s.name || s.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Month</Label>
            <Input type="month" data-testid="payroll-month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-2xl h-11 border-slate-200" />
          </div>

          {loading && !preview ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : preview ? (
            <div className="rounded-2xl bg-slate-50 p-4 text-sm space-y-1.5" data-testid="payroll-preview">
              <Row l="Monthly Salary" v={money(preview.monthly_salary)} />
              <Row l="Working Days" v={preview.working_days} />
              <Row l="Present / Half / Absent / Leave" v={`${preview.summary.present} / ${preview.summary["half-day"]} / ${preview.summary.absent} / ${preview.summary.leave}`} />
              <Row l="Per Day" v={money(preview.per_day)} />
              <Row l="Attendance Deduction" v={"- " + money(preview.attendance_deduction)} red />
              {(!preview.monthly_salary || preview.monthly_salary <= 0) && (
                <div data-testid="salary-zero-hint" className="mt-2 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Monthly salary is ₹0. Set it in the Staff tab before generating a payslip.</span>
                </div>
              )}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Bonus</Label>
              <Input type="number" data-testid="payroll-bonus" value={bonus} onChange={(e) => setBonus(e.target.value)} placeholder="0" className="rounded-2xl h-11 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Deductions</Label>
              <Input type="number" data-testid="payroll-deductions" value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="0" className="rounded-2xl h-11 border-slate-200" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Note</Label>
            <Input data-testid="payroll-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="rounded-2xl h-11 border-slate-200" />
          </div>

          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold text-slate-600">Net Pay</span>
            <span className="font-display font-bold text-xl text-emerald-600" data-testid="payroll-net">{money(net)}</span>
          </div>
          <Button onClick={generate} disabled={loading || !selected} data-testid="payroll-generate-btn"
            className="w-full rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working…</> : "Generate Payslip"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-0 rounded-3xl shadow-ark-soft lg:col-span-2">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-ark-ink flex items-center gap-2">
            <FileText className="h-5 w-5 text-ark-skyDeep" /> Payslips · {month}
          </h3>
          <div className="mt-4 space-y-3">
            {slips.length === 0 ? (
              <div className="py-10 text-center text-slate-500" data-testid="payslips-empty">No payslips for this month yet.</div>
            ) : slips.map((p) => (
              <div key={p.id} data-testid={`payslip-${p.staff_email}`} className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="min-w-0">
                  <div className="font-semibold text-ark-ink truncate">{p.staff_name || p.staff_email}</div>
                  <div className="text-xs text-slate-500">
                    {money(p.monthly_salary)} · −{money(p.attendance_deduction)} att · +{money(p.bonus)} bonus · −{money(p.deductions)} ded
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="font-display font-bold text-lg text-emerald-600">{money(p.net_pay)}</div>
                  <button
                    onClick={() => downloadSlip(p)}
                    aria-label={`Download payslip PDF for ${p.staff_name || p.staff_email}`}
                    data-testid={`payslip-download-${p.staff_email}`}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:text-ark-skyDeep hover:bg-sky-50 transition">
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeSlip(p)}
                    aria-label={`Delete payslip for ${p.staff_name || p.staff_email}`}
                    data-testid={`payslip-delete-${p.staff_email}`}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Row = ({ l, v, red }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{l}</span>
    <span className={`font-semibold ${red ? "text-red-500" : "text-slate-700"}`}>{v}</span>
  </div>
);

export default PayrollPanel;
